import { useEffect, useState } from 'react';
import { Badge, ButtonGroup, Button, Glyphicon, OverlayTrigger } from 'react-bootstrap';
import { IMoveTracker, IMoveTrackerStatus, IBaseResponse } from '@nkr/models/index';
import { MoveTrackerStatusType, TransportMethod } from '@nkr/enums/index';
import { RowWrapper, RowWrapperEmpty } from '../styles';
import { Utility } from '@nkr/services/utility';
import { Helper } from './helper';
import { NotesWrapper } from './styles';
import { truck } from 'react-icons-kit/fa/truck';
import { airplane } from 'react-icons-kit/icomoon/airplane';
import Icon from 'react-icons-kit';
import PopoverTemplate from '@nkr/components/popover-template';

interface IProps {
    item: IMoveTracker,
    index: number,
    useBackupPlan: boolean,
    displayTimeZone: string,
    localTimeZone: string,
    backgroundColor?: string,
    textColor?: string,
    borderColor?: string,
    enableNotes?: boolean,
    isCourier: boolean,
    onViewLogistics: (item: IMoveTracker) => void,
    onShowMap: (url: string) => void,
    onShowGps: (link: string) => void,
    onSwitchPlan: (item: IMoveTracker) => void,
    onSaveNote: (donorId: number, notes: string) => Promise<IBaseResponse>,
    onAcknowledgeAlert: (chainId: number, clusterNumber: number, positionNumber: number) => void
}

interface IState {
    showNote: boolean,
    editNote: boolean,
    notes: string
}

const MoveTrackerListRow = (props: IProps) => {
    const [showNote, setShowNote] = useState(false);
    const [editNote, setEditNote] = useState(false);
    const [notes, setNotes] = useState('');

    useEffect(() => {
        setNotes(props.item.notes);
    }, [props]);

    const handleViewLogistics = () => {
        props.onViewLogistics(props.item);
    };

    const handleShowMap = () => {
        var url = props.item.mapLink;

        props.onShowMap(url);
    };

    const handleShowGps = () => {
        props.onShowGps(props.item.gpsAutomation.gpsLink);
    };

    const handleSwitchPlan = () => {
        if (props.item.logistics.transportMethodId != TransportMethod.Ground && props.item.logistics.transportMethodId != TransportMethod.Internal) {
            setShowNote(false);
            setEditNote(false);
            props.onSwitchPlan(props.item);
        }
    };

    const handleEditNote = () => {
        setEditNote(true);
    };

    const handleSaveNote = () => {
        props.onSaveNote(props.item.donorId, notes).then(() => {
            setEditNote(false);
        });
    };

    const handleCancelNote = () => {
        setEditNote(false);
        setNotes(props.item.notes);
    };

    const handleShowNote = () => {
        setShowNote(!showNote);
    };

    const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setNotes(e.currentTarget.value);
    };

    const handleAcknowledgeAlert = () => {
        let { chainId, clusterNumber, positionNumber } = props.item;

        props.onAcknowledgeAlert(chainId, clusterNumber, positionNumber);
    };

    const getTimezone = (timezone: string) => {
        if (timezone.toUpperCase() == 'PHO'){
            return 'MST*';
        }

        return timezone;
    };

    const renderSection = (label: string, transportMethod: string, item: IMoveTrackerStatus, transportList: string[]) => {
        var { fillColor, textColor, badgeColor } = Helper.getStatusColor(item.status, item.complete);

        if (props.displayTimeZone == 'DONORCENTER') {
            let actual = item.actualDateTime ? `${item.actualDateTime} ${item.actualDateTimeTz}` : '';
            let schedule = item.scheduleDateTime ? `${item.scheduleDateTime} ${item.scheduleDateTimeTz}` : '';

            label = item.complete ? actual : schedule;
        } else {
            label = item.complete ? item.actualDateTimeLocal : item.scheduleDateTimeLocal;
        }

        label = (transportList.includes(transportMethod) ? 'N/A' : label);

        let height = props.backgroundColor ? 25 : 31;
        let badgeHeight = props.backgroundColor ? 19 : 21;
        let borderRadius = props.backgroundColor ? 4 : 0;
        let badgeLineHeight = props.backgroundColor ? '12px' : 1.4;

        // let st = moment().year() + '-11-01';
        // let ed = moment().year() + '-11-30';

        if (item.status != -1 && label != 'N/A')
            return (
                <div style={{ height, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', lineHeight: 0, color: textColor, backgroundColor: fillColor, borderRadius }}>
                    <Badge style={{ backgroundColor: badgeColor, color: textColor, height: badgeHeight, lineHeight: badgeLineHeight, borderRadius: 4 }}>{label}</Badge>

                    {item.type == MoveTrackerStatusType.FlightLeg1 && item.tenderToAirlineDateTimeActual != '' && (
                        <span style={{ paddingLeft: 5 }} title={`Tendered to airline @ ${item.tenderToAirlineDateTimeActual} ${item.tenderToAirlineDateTimeActualTz}`}><Icon icon={truck} /></span>
                    )}

                    {item.type == MoveTrackerStatusType.FlightLeg1 && item.arrivalDateTimeActual != '' && (
                        <span style={{ paddingLeft: 5 }} title={`Flight arrived @ ${item.arrivalDateTimeActual} ${item.arrivalDateTimeActualTz}`}><Icon icon={airplane} /></span>
                    )}
                </div>
            );
        else
            return (
                <div style={{ height, width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', lineHeight: 0, color: textColor, backgroundColor: fillColor, fontSize: 12, borderRadius }}>
                    {label}
                </div>
            );
    };

    const item = props.item;
    const transportMethod = item.logistics.transportMethod.toUpperCase();
    const mapButtonStyle = Utility.getMapButtonStyle(item.logistics);

    const donorTimezone = getTimezone(item.rdnCenterId == 0 ? item.donorCenterTimezone : item.rdnCenterTimezone);

    const kidneyPickup = (props.displayTimeZone == 'DONORCENTER' ? item.kidneyPickup + ' ' + (donorTimezone) : item.kidneyPickupLocal + ' ' + props.localTimeZone);
    const kidneyArrival = (props.displayTimeZone == 'DONORCENTER' ? item.kidneyArrival + ' ' + item.recipientCenterTimezone : item.kidneyArrivalLocal + ' ' + props.localTimeZone);
    const enableNotes = props.enableNotes != undefined ? props.enableNotes : true;

    const DutyOfficerOverlay = (
        <PopoverTemplate>
            <table>
                <tbody>
                    <tr>
                        <td>Name:</td>
                        <td>{item.dutyOfficerName}</td>
                    </tr>
                    <tr>
                        <td>Email:</td>
                        <td>{item.dutyOfficerEmail}</td>
                    </tr>
                    <tr>
                        <td>Phone:</td>
                        <td>{item.dutyOfficerPhone}</td>
                    </tr>
                </tbody>
            </table>
        </PopoverTemplate>
    );

    return (
        <>
            <RowWrapper
                useBackupPlan={props.useBackupPlan}
                backgroundColor={props.backgroundColor}
                textColor={props.textColor}
                borderColor={props.borderColor}
            >
                <td>{props.index + 1}</td>
                <td>
                    {!item.activeAlert ?
                        <OverlayTrigger placement={'right'} trigger='click' rootClose={true} overlay={DutyOfficerOverlay}>
                            <span>{item.dutyOfficer}</span>
                        </OverlayTrigger>
                        :
                        <Button bsStyle='danger' bsSize='xs' onClick={handleAcknowledgeAlert}>{item.dutyOfficer}</Button>
                    }
                </td>
                <td style={{ cursor: 'pointer' }} onClick={handleShowNote}>{item.chainId}/{item.clusterNumber}/{item.positionNumber}</td>
                <td style={{ cursor: 'pointer' }} onClick={handleShowNote}>
                    {item.donorCenter}
                    {item.rdnCenterId > 0 && (
                        <span style={{ paddingLeft: 5 }}>(<span style={{ fontSize: 12, color: 'tomato', fontWeight: 600 }}>{item.rdnCenterAlias}</span>)</span>
                    )}
                </td>
                <td style={{ cursor: 'pointer' }} onClick={handleShowNote}>{kidneyPickup}</td>
                <td style={{ cursor: 'pointer' }} onClick={handleShowNote}>{item.recipientCenter}</td>
                <td style={{ cursor: 'pointer' }} onClick={handleShowNote}>{kidneyArrival}</td>

                {props.isCourier == false && (
                    <td style={{ cursor: 'pointer' }} onClick={handleShowNote}>{item.courier}</td>
                )}

                <td>
                    <ButtonGroup>
                        <Button bsSize='xs' style={{ minWidth: 25, padding: '1px 3px', fontSize: 10 }} onClick={handleViewLogistics}>L</Button>
                        <Button bsSize='xs' bsStyle={mapButtonStyle} style={{ minWidth: 25, padding: '1px 3px', fontSize: 10 }} onClick={handleShowMap}>M</Button>
                        {item.gpsAutomation != null && item.gpsAutomation.gpsLink != '' ?
                            <Button bsSize='xs' style={{ minWidth: 25, padding: '1px 3px', fontSize: 10 }} onClick={handleShowGps}>G{item.gpsAutomation.gpsNumber}</Button>
                            : null}
                    </ButtonGroup>
                </td>

                <td style={{ cursor: 'pointer' }} onClick={handleSwitchPlan}>{item.logistics.transportMethod}</td>

                <td style={{ borderRight: '1px solid #eee' }}>
                    {renderSection('Pickup', transportMethod, item.pickup, [])}
                </td>

                <td style={{ borderRight: '1px solid #eee' }}>
                    {renderSection('Flight 1', transportMethod, item.flightDeparture1, ['GROUND'])}
                </td>

                <td style={{ borderRight: '1px solid #eee' }}>
                    {renderSection('Flight 2', transportMethod, item.flightDeparture2, ['GROUND', 'COMMERCIAL', 'CHARTER', 'COMMONELEGOBC'])}
                </td>

                <td style={{ borderRight: '1px solid #eee' }}>
                    {renderSection('Recovery', transportMethod, item.airlineRecovery, ['GROUND'])}
                </td>

                <td style={{ borderRight: '1px solid #eee' }}>
                    {renderSection('Delivery', transportMethod, item.delivery, [])}
                </td>
            </RowWrapper>

            {showNote && enableNotes && (
                <RowWrapperEmpty>
                    <td colSpan={15}>
                        {editNote ?
                            <div style={{ width: '100%', border: '1px solid #ddd', padding: 5, display: 'flex' }}>
                                <div style={{ flexBasis: '95%' }}>
                                    <textarea className='form-control' style={{ width: '100%', height: 70, resize: 'none' }} value={notes} onChange={handleNotesChange}></textarea>
                                </div>
                                <div style={{ flexBasis: '5%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end' }}>
                                    <button className='btn btn-default btn-sm' style={{ margin: '0px 5px 2px 0px' }} onClick={handleSaveNote}><Glyphicon glyph='ok' style={{ color: '#A1A1A1' }} /></button>
                                    <button className='btn btn-default btn-sm' style={{ margin: '0px 0px 2px 0px' }} onClick={handleCancelNote}><Glyphicon glyph='remove' style={{ color: '#A1A1A1' }} /></button>
                                </div>
                            </div>
                            :
                            <>
                                {notes != '' ?
                                    <NotesWrapper style={{ cursor: 'default' }} onClick={handleEditNote}>
                                        {notes.split('\n').map((line, index) =>
                                            line != '' ?
                                                <div key={index}>{line}</div>
                                                :
                                                <div key={index}>&nbsp;</div>
                                        )}
                                    </NotesWrapper>
                                    :
                                    <NotesWrapper style={{ cursor: 'default' }} onClick={handleEditNote}>[click here to enter notes...]</NotesWrapper>
                                }
                            </>
                        }
                    </td>
                </RowWrapperEmpty>
            )}
        </>
    );
}

export default MoveTrackerListRow;